import { FLOAT_DEFAULT_SIZE, FLOAT_MIN_SIZE, PanelDockSide, PanelPlacement } from './panelDockStore.ts';

export interface PanelRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Space kept between a floating window and the viewport edge when it is placed for the user. */
const FLOAT_GUTTER = 16;
/**
 * How far a floating window has to be pushed past a screen edge — it stops at the edge, the pointer
 * keeps going — before releasing it docks it there.
 */
const SNAP_OVERSHOOT = 32;
/**
 * The pointer cannot leave the viewport, so a window grabbed near its own edge could never be pushed
 * SNAP_OVERSHOOT past it. Reaching the screen edge with the pointer, window already against it, counts.
 */
const POINTER_EDGE = 8;

const cssPx = (name: string): number => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;

/**
 * The right edge of the menu rail, which is where a left-docked panel starts. Published by
 * useShellMetrics; 0 on layouts that do not publish it.
 */
export const menuRailEdge = (): number => Math.max(0, cssPx('--app-content-left') - cssPx('--app-gutter'));

/**
 * Keeps a floating window usable: at least its minimum size, at most the viewport, and entirely on
 * screen. The same bounds resizeFloatRect uses, so a window resized edge to edge stays that size.
 */
export const clampFloatRect = ({ x, y, w, h }: PanelRect): PanelRect => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const cw = Math.max(FLOAT_MIN_SIZE.w, Math.min(w, vw));
  const ch = Math.max(FLOAT_MIN_SIZE.h, Math.min(h, vh));
  return {
    w: cw,
    h: ch,
    x: Math.max(0, Math.min(x, vw - cw)),
    y: Math.max(0, Math.min(y, vh - ch)),
  };
};

/**
 * Where a panel floats: its remembered geometry, or — the first time — the default size in the bottom
 * corner of the edge it was docked to, so it does not jump across the screen.
 */
export const floatRectFor = (placement: PanelPlacement): PanelRect => {
  const w = placement.w ?? FLOAT_DEFAULT_SIZE.w;
  const h = placement.h ?? FLOAT_DEFAULT_SIZE.h;
  if (placement.x != null && placement.y != null) return clampFloatRect({ x: placement.x, y: placement.y, w, h });

  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const x = placement.side === 'left' ? menuRailEdge() + FLOAT_GUTTER : vw - w - FLOAT_GUTTER;
  return clampFloatRect({ x, y: vh - h - FLOAT_GUTTER, w, h });
};

/**
 * The dock a drag would end in if released now — or null for "stay floating".
 *
 * Takes where the window *would* be if it were not held on screen (pointer minus grab offset), so the
 * rule is "push it against the edge, then a bit more". Measuring the pointer against the edge instead
 * made it depend on where the header was grabbed: grabbed in the middle, the window hit the edge while
 * the pointer was still half a window away.
 */
export const snapSideFor = (unclamped: PanelRect, pointerX: number): PanelDockSide | null => {
  const vw = document.documentElement.clientWidth;
  const overLeft = -unclamped.x;
  const overRight = unclamped.x + unclamped.w - vw;
  if (overLeft > SNAP_OVERSHOOT || (overLeft >= 0 && pointerX <= POINTER_EDGE)) return 'left';
  if (overRight > SNAP_OVERSHOOT || (overRight >= 0 && pointerX >= vw - POINTER_EDGE)) return 'right';
  return null;
};

/** A resize handle: the edges it moves — 'se' is the bottom-right corner. */
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));

/**
 * The window after dragging `edge` by (dx, dy) from `start`. The opposite edge stays put, and the result
 * keeps the minimum size and stays on screen — a west or north edge stops rather than pushing the
 * window, so it never slides while being resized.
 */
export const resizeFloatRect = (start: PanelRect, edge: ResizeEdge, dx: number, dy: number): PanelRect => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  let { x, y, w, h } = start;
  if (edge.includes('e')) w = clamp(start.w + dx, FLOAT_MIN_SIZE.w, vw - start.x);
  if (edge.includes('w')) {
    const right = start.x + start.w;
    x = clamp(start.x + dx, 0, right - FLOAT_MIN_SIZE.w);
    w = right - x;
  }
  if (edge.includes('s')) h = clamp(start.h + dy, FLOAT_MIN_SIZE.h, vh - start.y);
  if (edge.includes('n')) {
    const bottom = start.y + start.h;
    y = clamp(start.y + dy, 0, bottom - FLOAT_MIN_SIZE.h);
    h = bottom - y;
  }
  return { x, y, w, h };
};
